import React, { useEffect, useState } from "react";

const tableStyle = {
    borderCollapse: "collapse",
    width: "100%",
    marginBottom: "2rem",
};

const thTdStyle = {
    padding: "12px 24px",
    border: "1px solid #ddd",
    textAlign: "left",
};

const UserReport = () => {
    // Only allow admin users to view this report
    const accountType = sessionStorage.getItem("account_type");
    if (accountType !== "admin") {
        return null; // Or: return <div>Access denied.</div>;
    }

    const [report, setReport] = useState(null);
    const [trend, setTrend] = useState([]);
    const [trendTooltip, setTrendTooltip] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // No user_id is sent from frontend; backend uses flask_login for user context
        fetch(`${process.env.REACT_APP_API_URL}/api/user_report`, {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => {
                setReport(data);
                setLoading(false);
            });

        fetch(`${process.env.REACT_APP_API_URL}/api/user_trend`, {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => {
                setTrend(data.data);
                setTrendTooltip(data.tooltip);
            });
    }, []);

    if (loading) return <div>Loading user report...</div>;
    if (!report) return <div>No data available.</div>;

    const userTooltip = report.top_users?.[0]?.tooltip || "";
    const moduleTooltip = report.top_modules?.[0]?.tooltip || "";
    const screenTooltip = report.top_screens?.[0]?.tooltip || "";

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        return `${mm}/${dd}/${yy}`;
    };

    return (
        <div>
            <h2 title={userTooltip}>Top 5 Most Active Users</h2>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thTdStyle}>Email</th>
                        <th style={thTdStyle}>Activity Count</th>
                        <th style={thTdStyle}>Active Since</th>
                        <th style={thTdStyle}>Oldest Day</th>
                    </tr>
                </thead>
                <tbody>
                    {(report.top_users || []).map((user, idx) => (
                        <tr key={idx}>
                            <td style={thTdStyle}>{user.email}</td>
                            <td style={thTdStyle}>{user.activity_count}</td>
                            <td style={thTdStyle}>{formatDate(user.active_since)}</td>
                            <td style={thTdStyle}>{user.oldest_day}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <h2 title={moduleTooltip}>Top 10 Modules</h2>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thTdStyle}>Module Name</th>
                        <th style={thTdStyle}>Unique Users</th>
                    </tr>
                </thead>
                <tbody>
                    {(report.top_modules || []).map((mod, idx) => (
                        <tr key={idx}>
                            <td style={thTdStyle}>{mod.module_name}</td>
                            <td style={thTdStyle}>{mod.unique_users}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <h2 title={screenTooltip}>Top 10 Screens</h2>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thTdStyle}>Screen Name</th>
                        <th style={thTdStyle}>Unique Users</th>
                    </tr>
                </thead>
                <tbody>
                    {(report.top_screens || []).map((screen, idx) => (
                        <tr key={idx}>
                            <td style={thTdStyle}>{screen.screen_name}</td>
                            <td style={thTdStyle}>{screen.unique_users}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <h2 title={trendTooltip}>Trending Unique Users by Day</h2>
            <div style={{ width: "100%", height: "300px", marginBottom: "2rem" }}>
                <svg width="100%" height="300">
                    {/* Simple line chart rendering */}
                    {trend.length > 1 && (() => {
                        const w = 800, h = 300, pad = 40;
                        const maxY = Math.max(...trend.map(d => d.unique_users));
                        const minY = Math.min(...trend.map(d => d.unique_users));
                        const yRange = maxY - minY || 1;
                        const xStep = (w - pad * 2) / (trend.length - 1);
                        const points = trend.map((d, i) => {
                            const x = pad + i * xStep;
                            const y = h - pad - ((d.unique_users - minY) / yRange) * (h - pad * 2);
                            return [x, y];
                        });
                        return (
                            <>
                                <polyline
                                    fill="none"
                                    stroke="#8884d8"
                                    strokeWidth="2"
                                    points={points.map(p => p.join(",")).join(" ")}
                                />
                                <text x={pad} y={pad} fontSize="14" fill="#8884d8">Unique Users</text>
                                {/* X axis labels */}
                                {points.map((p, i) => (
                                    <text key={i} x={p[0]} y={h - pad + 15} fontSize="10" textAnchor="middle">
                                        {trend[i].date.slice(5)}
                                    </text>
                                ))}
                                {/* Y axis labels */}
                                {[minY, maxY].map((yVal, i) => (
                                    <text key={i} x={5} y={h - pad - ((yVal - minY) / yRange) * (h - pad * 2)} fontSize="10">
                                        {yVal}
                                    </text>
                                ))}
                            </>
                        );
                    })()}
                </svg>
            </div>
        </div>
    );
};

export default UserReport;